import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Activity, AlertCircle, Bell, CheckCircle, Clock, ArrowUpDown, ChevronDown } from 'lucide-react';
import './Notifications.css';

interface Notification {
  id: number;
  icon: React.ReactNode;
  title: string;
  message: string;
  time: string;
  type: 'info' | 'warning' | 'success' | 'alert';
  read: boolean;
}

type SortOption = 'newest' | 'oldest' | 'unread' | 'alert' | 'info' | 'success' | 'warning';

const SORT_LABELS: Record<SortOption, string> = {
  newest: 'Newest First',
  oldest: 'Oldest First',
  unread: 'Unread First',
  alert: 'Alerts Only',
  info: 'Info Only',
  success: 'Success Only',
  warning: 'Warnings Only',
};

const Notifications: React.FC = () => {
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allNotifications: Notification[] = [
    {
      id: 1,
      icon: <Calendar size={20} />,
      title: 'Upcoming Doctor\'s Appointment',
      message: 'You have an appointment with Dr. Smith scheduled for tomorrow at 2:00 PM.',
      time: '2 hours ago',
      type: 'info',
      read: false,
    },
    {
      id: 2,
      icon: <AlertCircle size={20} />,
      title: 'Detected High A1C Level',
      message: 'Your recent lab results show an A1C level of 7.2%. Please consult with your healthcare provider.',
      time: '5 hours ago',
      type: 'alert',
      read: false,
    },
    {
      id: 3,
      icon: <Activity size={20} />,
      title: 'Daily Step Goal Achieved',
      message: 'Congratulations! You\'ve reached your daily step goal of 10,000 steps.',
      time: '1 day ago',
      type: 'success',
      read: true,
    },
    {
      id: 4,
      icon: <Bell size={20} />,
      title: 'Medication Reminder',
      message: 'Time to take your evening medication. Don\'t forget to log it in your tracker.',
      time: '1 day ago',
      type: 'warning',
      read: true,
    },
    {
      id: 5,
      icon: <Clock size={20} />,
      title: 'Glucose Check Reminder',
      message: 'It\'s been 4 hours since your last glucose check. Time for another reading.',
      time: '2 days ago',
      type: 'info',
      read: true,
    },
    {
      id: 6,
      icon: <CheckCircle size={20} />,
      title: 'Weekly Report Ready',
      message: 'Your weekly health summary is now available. Review your progress and trends.',
      time: '3 days ago',
      type: 'success',
      read: true,
    },
    {
      id: 7,
      icon: <AlertCircle size={20} />,
      title: 'Low Glucose Alert',
      message: 'Your glucose level dropped to 65 mg/dL. Consider having a snack.',
      time: '4 days ago',
      type: 'alert',
      read: true,
    },
    {
      id: 8,
      icon: <Calendar size={20} />,
      title: 'Lab Work Scheduled',
      message: 'Your quarterly lab work has been scheduled for next Monday at 9:00 AM.',
      time: '5 days ago',
      type: 'info',
      read: true,
    },
  ];

  // ── Sort logic ──────────────────────────────────────────────
  const sorted = [...allNotifications].sort((a, b) => {
    if (sortBy === 'unread') return Number(a.read) - Number(b.read);
    if (sortBy === 'oldest') return a.id - b.id;
    return b.id - a.id; // newest default
  }).filter(n => {
    if (sortBy === 'alert') return n.type === 'alert';
    if (sortBy === 'info') return n.type === 'info';
    if (sortBy === 'success') return n.type === 'success';
    if (sortBy === 'warning') return n.type === 'warning';
    return true;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'alert':
        return '#ff5252';
      case 'warning':
        return '#ffa726';
      case 'success':
        return '#66bb6a';
      case 'info':
      default:
        return '#00bcd4';
    }
  };

  return (
    <div className="notifications">
      <div className="notifications-header-row">
        <h1 className="notifications-title">Notifications</h1>

        {/* Sort dropdown */}
        <div className="sort-dropdown-wrapper" ref={dropdownRef}>
          <button
            className="sort-btn"
            onClick={() => setDropdownOpen(o => !o)}
          >
            <ArrowUpDown size={15} />
            {SORT_LABELS[sortBy]}
            <ChevronDown size={14} className={`sort-chevron ${dropdownOpen ? 'open' : ''}`} />
          </button>
          {dropdownOpen && (
            <div className="sort-menu">
              {(Object.keys(SORT_LABELS) as SortOption[]).map(opt => (
                <button
                  key={opt}
                  className={`sort-option ${sortBy === opt ? 'active' : ''}`}
                  onClick={() => { setSortBy(opt); setDropdownOpen(false); }}
                >
                  {SORT_LABELS[opt]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="notifications-list">
        {sorted.length === 0 && (
          <p className="no-notifications">No notifications match this filter.</p>
        )}
        {sorted.map((notification) => (
          <div 
            key={notification.id} 
            className={`notification-item ${notification.read ? 'read' : 'unread'}`}
          >
            <div 
              className="notification-icon" 
              style={{ backgroundColor: `${getTypeColor(notification.type)}20`, color: getTypeColor(notification.type) }}
            >
              {notification.icon}
            </div>
            <div className="notification-content">
              <div className="notification-header">
                <h3 className="notification-title">{notification.title}</h3>
                <span className="notification-time">{notification.time}</span>
              </div>
              <p className="notification-message">{notification.message}</p>
            </div>
            {!notification.read && <div className="unread-indicator"></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notifications;
